/**
 * Driver-facing privacy screen: declare periods during which nothing is
 * recorded about your vehicle.
 *
 * The promise made to the driver is that data is never COLLECTED, not that it
 * is hidden from their manager — the wording throughout says so, because a
 * driver who believes their positions are merely masked has not been reassured
 * at all.
 *
 * Dates are sent as wall clock ("YYYY-MM-DDTHH:MM") and resolved server-side
 * against the tenant timezone; see privacyApi.createPeriod.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield, Plus, X, Loader2, AlertCircle, Clock, Car, Play, Trash2 } from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import clsx from 'clsx';
import { privacyApi, type MyPrivacyDevice, type PrivacyPeriod, type PrivacyReason } from '../services/api';
import { privacySummary, type WorkSchedule } from './WorkScheduleEditor';

/**
 * Closed list, mirroring the server enum. Free text is deliberately impossible:
 * a driver typing "sick leave" would disclose health data — a GDPR special
 * category — to the manager, who is shown this reason.
 */
const REASON_LABELS: Record<PrivacyReason, string> = {
  LEAVE: 'Congés',
  WEEKLY_REST: 'Repos hebdomadaire',
  PERSONAL_USE: 'Usage personnel',
  OTHER: 'Autre',
};

/** Wall-clock formatting — never toISOString(), which would shift to UTC. */
const toWall = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm");

/**
 * Presets in the driver's own local time. This is wall clock all the way to the
 * server, so no timezone maths happens here — the failure mode this avoids is a
 * preset silently landing an hour off across a DST boundary.
 */
function buildPresets() {
  const now = new Date();
  const tomorrow8am = new Date(addDays(now, 1));
  tomorrow8am.setHours(8, 0, 0, 0);

  const nextMonday = startOfWeek(addDays(now, 7), { weekStartsOn: 1 });
  const nextSunday = endOfWeek(addDays(now, 7), { weekStartsOn: 1 });

  return [
    {
      label: "Jusqu'à demain matin",
      start: toWall(now),
      end: toWall(tomorrow8am),
    },
    {
      label: 'Cette semaine',
      start: toWall(now),
      end: toWall(endOfWeek(now, { weekStartsOn: 1 })),
    },
    {
      label: 'Semaine prochaine',
      start: toWall(nextMonday),
      end: toWall(nextSunday),
    },
  ];
}

function formatRange(startsAt: string, endsAt: string) {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  return `${format(start, 'd MMM à HH:mm', { locale: fr })} → ${format(end, 'd MMM à HH:mm', { locale: fr })}`;
}

/** What the company schedule means for this driver, in plain French. */
function workSummary(device: MyPrivacyDevice): string | null {
  const w = device.work;
  if (!w || !w.enabled || !w.workSchedule) return null;
  return privacySummary(w.workSchedule as WorkSchedule);
}

export default function DriverPrivacySection() {
  const queryClient = useQueryClient();
  const [openFor, setOpenFor] = useState<string | null>(null);

  const { data: devices, isLoading } = useQuery({
    queryKey: ['my-privacy'],
    queryFn: privacyApi.getMine,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
      </div>
    );
  }

  // A user with no assigned vehicle has nothing to declare. The tab is hidden
  // in that case, so this is only a safety net.
  if (!devices || devices.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        Aucun véhicule ne vous est attribué : vous n'avez pas de période de confidentialité à
        déclarer.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center mb-1">
          <Shield className="h-5 w-5 text-gray-400 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Ma vie privée</h3>
        </div>
        <p className="text-sm text-gray-500">
          Pendant une période de confidentialité, <strong>aucune position de votre véhicule
          n'est enregistrée</strong>. Les données ne sont pas masquées à votre employeur : elles
          ne sont jamais collectées. Une période ne peut pas commencer dans le passé.
        </p>
      </div>

      {devices.map((device) => (
        <DeviceCard
          key={device.id}
          device={device}
          isFormOpen={openFor === device.id}
          onToggleForm={() => setOpenFor(openFor === device.id ? null : device.id)}
          onDone={() => {
            setOpenFor(null);
            queryClient.invalidateQueries({ queryKey: ['my-privacy'] });
          }}
        />
      ))}
    </div>
  );
}

function DeviceCard({
  device,
  isFormOpen,
  onToggleForm,
  onDone,
}: {
  device: MyPrivacyDevice;
  isFormOpen: boolean;
  onToggleForm: () => void;
  onDone: () => void;
}) {
  const summary = workSummary(device);

  return (
    <div className="border border-gray-200 rounded-xl p-4">
      {/* Stacked on mobile: side by side, the schedule summary and the button
          squeeze each other into unreadable columns on a phone. The button goes
          full width at the bottom, where a thumb reaches it. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Car className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="font-medium text-gray-900 truncate">{device.name}</span>
            {device.plate && <span className="text-sm text-gray-500">{device.plate}</span>}
          </div>

          {device.activeNow ? (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-medium">
              <Shield className="h-3.5 w-3.5" />
              Mode privé actif — aucune position enregistrée
            </div>
          ) : (
            <div className="mt-2 text-xs text-gray-500">Suivi actif</div>
          )}

          {summary && (
            <p className="mt-2 text-xs text-gray-500">
              <Clock className="h-3 w-3 inline mr-1" />
              Défini par votre entreprise : {summary}
            </p>
          )}
        </div>

        <button
          onClick={onToggleForm}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2.5 text-sm text-white transition-colors hover:bg-primary-700 sm:w-auto sm:flex-shrink-0 sm:py-2"
        >
          {isFormOpen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {isFormOpen ? 'Annuler' : 'Déclarer une absence'}
        </button>
      </div>

      {isFormOpen && <PeriodForm deviceId={device.id} onDone={onDone} />}

      {device.periods.length > 0 && (
        <ul className="mt-4 space-y-2">
          {device.periods.map((period) => (
            <PeriodRow key={period.id} period={period} onDone={onDone} />
          ))}
        </ul>
      )}
    </div>
  );
}

function PeriodRow({ period, onDone }: { period: PrivacyPeriod; onDone: () => void }) {
  const hasStarted = new Date(period.startsAt).getTime() <= Date.now();

  const cancel = useMutation({
    mutationFn: async () => {
      if (hasStarted) {
        // Already running: it cannot be deleted, only ended now. The record of
        // the period has to survive — it is what explains the gap in the
        // vehicle's history.
        await privacyApi.updatePeriod(period.id, { endNow: true });
        return;
      }
      await privacyApi.deletePeriod(period.id);
    },
    onSuccess: onDone,
  });

  return (
    <li
      className={clsx(
        'rounded-lg border px-3 py-3 text-sm',
        // A running period is the one thing on this screen actively changing
        // what is recorded, so it reads as live rather than as a list row.
        hasStarted ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-gray-50'
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium text-gray-900">{REASON_LABELS[period.reason]}</span>
        {hasStarted && (
          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white">
            <Shield className="h-3 w-3" />
            en cours
          </span>
        )}
      </div>

      <p className="mt-0.5 text-xs text-gray-600">{formatRange(period.startsAt, period.endsAt)}</p>

      {/* Full width and touch-sized on mobile: ending a running period early is
          a real decision, and as a grey text link it was easy to miss entirely. */}
      <button
        onClick={() => cancel.mutate()}
        disabled={cancel.isPending}
        className={clsx(
          'mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 sm:mt-2 sm:w-auto sm:py-2',
          hasStarted
            ? 'border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-100'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-red-600'
        )}
      >
        {cancel.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : hasStarted ? (
          <Play className="h-4 w-4" />
        ) : (
          <Trash2 className="h-4 w-4" />
        )}
        {cancel.isPending
          ? 'Enregistrement…'
          : hasStarted
            ? 'Terminer maintenant'
            : 'Supprimer'}
      </button>

      {hasStarted && (
        <p className="mt-1.5 text-[11px] text-gray-500">
          Le suivi reprendra immédiatement. La période reste inscrite dans l'historique.
        </p>
      )}
    </li>
  );
}

/** Reusable field wrapper so every editable control reads the same way. */
function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <span className="block text-sm font-medium text-gray-900">{label}</span>
      {children}
      {hint && <span className="block text-xs text-gray-500">{hint}</span>}
    </div>
  );
}

/**
 * Every control here is editable, which the first version failed to convey: the
 * dates and the reason looked like a printed summary. Fields are now labelled
 * in the same weight, sit on a white background inside the grey panel, and the
 * chosen values are restated in plain French at the bottom so the driver sees
 * exactly what they are about to hide before confirming.
 */
function PeriodForm({ deviceId, onDone }: { deviceId: string; onDone: () => void }) {
  const presets = buildPresets();
  const [startsAt, setStartsAt] = useState(presets[0].start);
  const [endsAt, setEndsAt] = useState(presets[0].end);
  const [reason, setReason] = useState<PrivacyReason>('LEAVE');

  const create = useMutation({
    mutationFn: () => privacyApi.createPeriod({ deviceId, startsAt, endsAt, reason }),
    onSuccess: onDone,
  });

  const serverError =
    (create.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error ??
    (create.isError ? "Échec de l'enregistrement" : null);

  // `appearance-none` is the part that matters on iOS: Safari gives
  // datetime-local an intrinsic width derived from the locale format and
  // ignores width:100% until its native appearance is switched off, so the
  // control spills out of the panel on a phone.
  const inputClass =
    'block w-full min-w-0 max-w-full appearance-none rounded-lg border border-gray-300 bg-white px-2.5 py-2.5 text-sm leading-tight text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 sm:px-3';

  const rangeValid = endsAt > startsAt;

  return (
    <div className="mt-4 space-y-5 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <Field label="Durée" hint="Choisissez un raccourci, ou saisissez vos propres dates ci-dessous.">
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => {
            const active = startsAt === preset.start && endsAt === preset.end;
            return (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  setStartsAt(preset.start);
                  setEndsAt(preset.end);
                }}
                className={clsx(
                  'rounded-lg border px-3 py-2 text-xs transition-colors',
                  active
                    ? 'border-primary-500 bg-primary-600 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                )}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </Field>

      <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Début">
          <input
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Fin">
          <input
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            className={inputClass}
          />
        </Field>
      </div>

      {!rangeValid && (
        <p className="text-xs text-red-600">La fin doit être postérieure au début.</p>
      )}

      {/* Buttons rather than a native select: the list is short and closed, and
          showing all four at once is what makes it obvious the reason is a
          choice among fixed options, not free text the driver has to compose. */}
      <Field
        label="Motif"
        hint="Votre gestionnaire voit ce motif. La liste est volontairement fermée : elle vous évite d'avoir à divulguer un motif personnel ou médical."
      >
        <div className="grid grid-cols-2 gap-2">
          {(Object.keys(REASON_LABELS) as PrivacyReason[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setReason(key)}
              aria-pressed={reason === key}
              className={clsx(
                'rounded-lg border px-3 py-2.5 text-sm transition-colors',
                reason === key
                  ? 'border-primary-500 bg-primary-600 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
              )}
            >
              {REASON_LABELS[key]}
            </button>
          ))}
        </div>
      </Field>

      {rangeValid && (
        <div className="flex items-start gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
          <Shield className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            Aucune position ne sera enregistrée du{' '}
            <strong>{formatRange(startsAt, endsAt)}</strong>.
          </span>
        </div>
      )}

      {serverError && (
        <div className="flex items-start gap-2 text-sm text-red-600">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{serverError}</span>
        </div>
      )}

      <button
        onClick={() => create.mutate()}
        disabled={create.isPending || !rangeValid}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50 sm:w-auto sm:py-2"
      >
        {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
        Confirmer
      </button>
    </div>
  );
}
